import { AppState } from 'services/types';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import {
    makeSelectQueryStringNumberValue,
    makeSelectQueryStringValue
} from 'workspaces/react-router-selectors';
import {
    selectFirstDate,
    selectLastDate
    // @ts-ignore
} from 'components/DatePagination/selectors';
import {
    FertilizerEvent,
    FertilizerEventFilterType,
    FertilizerEventSearchFilters,
    FertilizerEventsState
} from './types';
import {
    FERTILIZER_DATE_FILTER_TYPE,
    FERTILIZER_DATE_RANGE_FILTER_TYPE,
    FERTILIZER_ENTITIES_FILTER_TYPE,
    FERTILIZER_INNER_ENTITIES_FILTER_TYPE,
    FERTILIZER_PRODUCT_FILTER_TYPE
} from './constants';

const selectFertilizerEvents = (state: AppState): FertilizerEventsState =>
    state.fertilizerEvents;

const selectFertilizerEventsArray = createDeepEqualSelector(
    selectFertilizerEvents,
    (fertilizerEvents) => Object.values(fertilizerEvents)
);

const selectFertilizerEventsSearchQueryString = createDeepEqualSelector(
    [
        makeSelectQueryStringNumberValue('fertilizerProductId'),
        makeSelectQueryStringValue('startDate'),
        makeSelectQueryStringValue('endDate'),
        makeSelectQueryStringNumberValue('entities'),
        makeSelectQueryStringNumberValue('innerEntities')
    ],
    (
        fertilizerProductId,
        startDate,
        endDate,
        fertilizerEntityId,
        fertilizerInnerEntityId
    ) =>
        ({
            fertilizerProductId,
            startDate,
            endDate,
            fertilizerEntityId,
            fertilizerInnerEntityId
        } as FertilizerEventSearchFilters)
);

const selectFertilizerEventsFilterType = createDeepEqualSelector(
    [selectFertilizerEventsSearchQueryString],
    (filters): FertilizerEventFilterType => {
        const {
            fertilizerProductId,
            startDate,
            endDate,
            fertilizerInnerEntityId,
            fertilizerEntityId
        } = filters;

        let filterType: FertilizerEventFilterType = FERTILIZER_DATE_FILTER_TYPE;

        if (fertilizerProductId) {
            filterType = FERTILIZER_PRODUCT_FILTER_TYPE;
        } else if (startDate && endDate) {
            filterType = FERTILIZER_DATE_RANGE_FILTER_TYPE;
        } else if (fertilizerInnerEntityId) {
            filterType = FERTILIZER_INNER_ENTITIES_FILTER_TYPE;
        } else if (fertilizerEntityId) {
            filterType = FERTILIZER_ENTITIES_FILTER_TYPE;
        }

        return filterType;
    }
);

const selectFilteredFertilizerEventsArray = createDeepEqualSelector(
    [
        selectFertilizerEventsArray,
        selectFertilizerEventsFilterType,
        selectFirstDate,
        selectLastDate,
        selectFertilizerEventsSearchQueryString
    ],
    (
        fertilizerEventsArray,
        filterType,
        firstDate: string,
        lastDate: string,
        searchFilters
    ) => {
        let filterFn;

        switch (filterType) {
            case FERTILIZER_PRODUCT_FILTER_TYPE: {
                const { fertilizerProductId } = searchFilters;
                filterFn = ({
                    fertilizerEvent: { fertilizerProducts }
                }: FertilizerEvent) => {
                    return fertilizerProducts.some(
                        ({ id }) => id === fertilizerProductId
                    );
                };
                break;
            }
            case FERTILIZER_DATE_RANGE_FILTER_TYPE: {
                const { startDate, endDate } = searchFilters as {
                    startDate: string;
                    endDate: string;
                };
                filterFn = (event: FertilizerEvent) =>
                    startDate <= event.startDate && event.startDate <= endDate;
                break;
            }

            case FERTILIZER_ENTITIES_FILTER_TYPE: {
                const { fertilizerEntityId } = searchFilters;
                filterFn = ({ entityId }: FertilizerEvent) => {
                    return entityId === fertilizerEntityId;
                };
                break;
            }
            case FERTILIZER_INNER_ENTITIES_FILTER_TYPE: {
                const { fertilizerInnerEntityId } = searchFilters;
                filterFn = ({
                    fertilizerEvent: { fertilizerEntities }
                }: FertilizerEvent) => {
                    return fertilizerEntities?.find(
                        (entity) => entity.id === fertilizerInnerEntityId
                    );
                };
                break;
            }
            default:
                filterFn = (event: FertilizerEvent) =>
                    firstDate <= event.startDate && event.startDate <= lastDate;
                break;
        }

        return fertilizerEventsArray.filter(filterFn);
    }
);

export {
    selectFertilizerEvents,
    selectFertilizerEventsArray,
    selectFertilizerEventsFilterType,
    selectFertilizerEventsSearchQueryString,
    selectFilteredFertilizerEventsArray
};
